import type { AccessLevel } from '../../api/settingsUsers'

/**
 * Treptele de acces care NU se mai ofera in formulare: vechiul Super Admin (4)
 * si GPS Dispatcher (5). Aceeasi lista ca `UsersSettings::HIDDEN_ACCESS_LEVELS`
 * din v4/api.
 *
 * Serverul le scoate deja din `accessLevels`, si le lasa numai pentru contul
 * care o are (marcata `hidden`). Filtrul de aici e plasa pentru listele scrise
 * in ecran (`ACCESS_LEVELS` din Authentication) si pentru un server mai vechi.
 */
export const HIDDEN_ACCESS_LEVELS: readonly number[] = [4, 5]

/**
 * Numele si "i"-ul fiecarui nivel (19 octombrie 2026) - aceleasi ca
 * `UsersSettings::ACCESS_LEVELS`. Numerele din baza nu s-au schimbat, numai
 * numele: 1 era "Administrator", 2 "Super User", 6 "IT Service Catalog only".
 * Nivelul spune CE FIRME vede omul; ce poate face acolo vine din rol.
 */
export const ACCESS_LEVEL_INFO: Readonly<Record<number, { label: string; help: string; short: string }>> = {
  1: {
    label: 'Admin',
    help: 'Sees only the companies of the offices the person is assigned to. What they can do there comes from the role.',
    short: 'the companies of the offices the person is assigned to',
  },
  2: {
    label: 'Super Admin',
    help: 'Sees the whole own company. What they can do comes from the role.',
    short: 'the whole own company',
  },
  3: {
    label: 'Service Provider',
    help: 'Sees all companies of the service provider. Given only by hand, never from the directory (IAM).',
    short: 'all companies of the provider (only by hand, never from IAM)',
  },
  6: {
    label: 'IT Service Catalog',
    help: 'Signs in only to the IT Service Catalog, not to the portal.',
    short: 'only the IT Service Catalog, not the portal',
  },
  4: {
    label: 'Super Admin (legacy)',
    help: 'Old level: sees the whole own company, like Super Admin. Kept for the accounts that have it; no longer given.',
    short: 'old level, like Super Admin; no longer given',
  },
  5: {
    label: 'GPS Dispatcher (legacy)',
    help: 'Old level: lands on GPS tracking. Kept for the accounts that have it; no longer given.',
    short: 'old level, lands on GPS tracking; no longer given',
  },
}

/** Numele unui nivel; un numar necunoscut se arata ca atare. */
export function accessLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'Not set'
  return ACCESS_LEVEL_INFO[value]?.label ?? `#${value}`
}

/** Textul "i"-ului unui nivel, din ce a trimis serverul sau din lista de aici. */
export function accessHelp(level: { value: number; help?: string }): string {
  return level.help ?? ACCESS_LEVEL_INFO[level.value]?.help ?? ''
}

/**
 * Descrierea scurta a unui nivel, pentru legenda (`AccessLevelLegend`): CE
 * FIRME vede omul, intr-o bucata de rand. Un nivel necunoscut cade pe textul
 * lung venit de pe server.
 */
export function accessShort(level: { value: number; help?: string }): string {
  return ACCESS_LEVEL_INFO[level.value]?.short ?? accessHelp(level)
}

const LEGACY = '(legacy)'

/**
 * Ce se ofera in lista: treptele vizibile, plus cea ascunsa numai daca e chiar
 * valoarea SALVATA (`current`) - ca o salvare a altui camp sa nu o schimbe.
 * `current` e valoarea venita de pe server, nu cea aleasa acum in formular:
 * altfel treapta ar disparea din lista la prima schimbare si nu s-ar mai putea
 * pune inapoi. Treapta ascunsa poarta "(legacy)" in nume.
 */
export function offeredAccessLevels(levels: readonly AccessLevel[], current: number | null): AccessLevel[] {
  return levels
    .filter((level) => !(level.hidden || HIDDEN_ACCESS_LEVELS.includes(level.value)) || level.value === current)
    .map((level) =>
      level.value === current && (level.hidden || HIDDEN_ACCESS_LEVELS.includes(level.value)) && !level.label.endsWith(LEGACY)
        ? /* Un server mai vechi scria "(hidden)": acelasi semn, cu numele nou. */
          { ...level, label: `${level.label.replace(/ \(hidden\)$/, '')} ${LEGACY}`, hidden: true }
        : level,
    )
}
